import type { CSSProperties } from 'react';
import type { DeleteEligibility, PublishState } from '@course-authoring/shared-types';
import {
  PublishActionCluster,
  type PublishClusterActions,
} from '../../components';
import { CourseDeleteAction } from '../../components/EditorDeleteAction';
import { formatCourseHumanId } from '../../utils/humanIds';
import type { CourseDraft } from './types';

interface CourseEditorHeaderProps {
  styles: Record<string, CSSProperties>;
  draft: CourseDraft;
  titleValue: string;
  publishTitle: string;
  isTranslationMode: boolean;
  isEditorLocked: boolean;
  currentEditorView: 'edit' | 'preview';
  saveStatusText: string;
  hasSaveError: boolean;
  isCreating: boolean;
  isOpeningPreview: boolean;
  /** NA-283: server-computed Delete availability for the top-bar action. */
  deleteEligibility: DeleteEligibility | undefined;
  /**
   * NA-283: whether NO client account context is active. Deliberately separate
   * from `clientContext` below, which is the *general-write* context and goes
   * null in a non-default locale even while an account is selected — reusing it
   * to gate Delete offered a base-scoped delete from inside a client context.
   */
  isStandardContext: boolean;
  clientContext: {
    accountId: number;
    accountName: string;
    accountSlug?: string;
  } | null;
  lessonTitlesById: Record<string, string>;
  onBack: () => void;
  onTitleChange: (title: string) => void;
  onTogglePreview: () => void;
  onBeforePublishAction: () => Promise<void>;
  onNavigateToLesson: (lessonId: string) => void;
  onPublishStateChange: (state: PublishState | null) => void;
  onRegisterPublishActions: (actions: PublishClusterActions) => void;
}

export function CourseEditorHeader({
  styles,
  draft,
  titleValue,
  publishTitle,
  isTranslationMode,
  isEditorLocked,
  currentEditorView,
  saveStatusText,
  hasSaveError,
  isCreating,
  isOpeningPreview,
  deleteEligibility,
  isStandardContext,
  clientContext,
  lessonTitlesById,
  onBack,
  onTitleChange,
  onTogglePreview,
  onBeforePublishAction,
  onNavigateToLesson,
  onPublishStateChange,
  onRegisterPublishActions,
}: CourseEditorHeaderProps) {
  const hasLessons = Object.keys(lessonTitlesById).length > 0;

  return (
    <header style={styles.topBar}>
      <button type="button" style={styles.backButton} onClick={onBack}>
        ← Courses
      </button>
      <input
        type="text"
        value={titleValue}
        onChange={(event) => onTitleChange(event.target.value)}
        style={styles.titleInput}
        aria-label="Course title"
        disabled={isTranslationMode || currentEditorView !== 'edit' || isEditorLocked}
      />
      <span style={styles.courseId}>{formatCourseHumanId(draft.numericId, draft.id)}</span>
      <span style={{ ...styles.saveStatus, ...(hasSaveError ? styles.saveStatusError : {}) }}>
        {saveStatusText}
      </span>
      <div style={styles.topActions}>
        <button
          type="button"
          style={styles.secondaryButton}
          onClick={onTogglePreview}
          disabled={isOpeningPreview}
        >
          {currentEditorView === 'preview'
            ? 'Edit Course'
            : isOpeningPreview
              ? 'Opening Preview...'
              : 'Preview Course'}
        </button>
        {!isTranslationMode && (
          <PublishActionCluster
            contentKind="course"
            packageId={draft.id}
            numericId={draft.numericId}
            title={publishTitle || 'Untitled Course'}
            publishState={draft.publishState}
            clientContext={clientContext}
            disabled={isCreating || isOpeningPreview || !hasLessons}
            // enforceSequence is independent of timedCourse and has no UI yet.
            enforceSequence={false}
            lessonTitlesById={lessonTitlesById}
            onBeforeAction={onBeforePublishAction}
            onNavigateToLesson={onNavigateToLesson}
            onPublishStateChange={onPublishStateChange}
            onRegisterActions={onRegisterPublishActions}
          />
        )}
        {/* Client context is the only case that withholds Delete (the spec's
            single exclusion); translation mode is still standard context, so it
            keeps the action. Gated on isStandardContext, NOT on clientContext:
            that prop tracks the general-write scope, which is null in every
            non-default locale even with an account selected. A course
            mid-creation has no server id to delete yet, hence isCreating. */}
        {!isCreating && (
          <CourseDeleteAction
            contentId={draft.id}
            title={publishTitle || titleValue}
            eligibility={deleteEligibility}
            isStandardContext={isStandardContext}
            disabled={isOpeningPreview}
          />
        )}
      </div>
    </header>
  );
}
